import { alpha } from "@mui/material";
import { createSxStyles } from "@common/createSxStyles";
import { borderRadius } from "../../privacyTheme.ts";
import { statusChipColors } from "@common/components/statusChip/StatusChip";

// Reuses the chip pair that already means "positive state" everywhere else (approved, signed, completed).
const sentGreen = statusChipColors.green;

export const publicRiskObservationStyles = createSxStyles({
    page: {
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        minHeight: 0,
        gap: 2,
    },
    topBar: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
    },
    stepChip: {
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        paddingX: 1.5,
        paddingY: 0.75,
        borderRadius: 999,
        fontSize: 13,
        fontWeight: 600,
        background: (theme) => theme.palette.grey[100],
        color: (theme) => theme.palette.primary.main,
    },
    stepChipSent: {
        background: sentGreen.background,
        color: sentGreen.text,
    },
    stepCounter: {
        color: (theme) => theme.palette.text.secondary,
        fontWeight: 400,
    },
    content: {
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        gap: 2,
    },
    // A shrunk outlined label sits 9px above its box, and getSpacing gives only 5px per unit below 600px width, so
    // the phone needs wider gaps than the desktop for the first label to clear the text above the group and the
    // second one to clear the field above it.
    fieldGroup: {
        display: "flex",
        flexDirection: "column",
        gap: { xs: 4, md: 2 },
        marginTop: { xs: 2, md: 0 },
    },
    welcomeIllustration: {
        alignSelf: "center",
        width: "100%",
        maxWidth: 154,
        aspectRatio: "154 / 124",
        marginTop: 3,
        marginBottom: 3,
    },
    benefitList: {
        listStyle: "none",
        margin: 0,
        padding: 0,
        display: "flex",
        flexDirection: "column",
        gap: 2,
    },
    benefitItem: {
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
    },
    benefitMarker: {
        flexShrink: 0,
        width: 24,
        height: 24,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 14,
        fontWeight: 700,
        background: (theme) => theme.palette.grey[100],
        color: (theme) => theme.palette.primary.main,
    },
    footer: {
        display: "flex",
        gap: 1.5,
        paddingTop: 2,
    },
    footerPrimary: {
        flexGrow: 1,
    },
    sourceCardList: {
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
    },
    summaryCard: {
        border: "0.5px solid",
        borderColor: "divider",
        borderRadius: `${borderRadius}px`,
        padding: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1,
    },
    summaryRow: {
        display: "flex",
        justifyContent: "space-between",
        gap: 2,
        fontSize: 14,
    },
    summaryLabel: {
        color: (theme) => theme.palette.text.secondary,
    },
    summaryValue: {
        fontWeight: 600,
        textAlign: "right",
        wordBreak: "break-word",
    },
    // A logo at full size outweighs both the step chip beside it and the .legal logo above the card on a
    // phone, so it is capped harder there. The breakpoint matches useIsOnSmallScreen.
    customerLogo: {
        maxWidth: { xs: 120, md: 180 },
        maxHeight: { xs: 32, md: 48 },
    },
    successMarker: {
        alignSelf: "center",
        width: 88,
        height: 88,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 44,
        background: sentGreen.background,
        color: sentGreen.text,
        marginTop: 2,
    },
});

export const sourceCardStyles = createSxStyles({
    card: {
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 2,
        textAlign: "left",
        padding: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        background: "white",
        cursor: "pointer",
    },
    iconTile: {
        flexShrink: 0,
        width: 48,
        height: 48,
        borderRadius: "14px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 24,
        lineHeight: 1,
        background: (theme) => alpha(theme.palette.primary.main, 0.08),
    },
    textBlock: {
        flexGrow: 1,
        minWidth: 0,
    },
    cardSelected: {
        borderColor: (theme) => theme.palette.primary.main,
        background: (theme) => theme.palette.grey[50],
    },
    name: {
        fontWeight: 600,
    },
    helperText: {
        fontSize: 13,
        color: (theme) => theme.palette.text.secondary,
    },
    radio: {
        flexShrink: 0,
        width: 22,
        height: 22,
        borderRadius: "50%",
        border: "2px solid",
        borderColor: "divider",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    radioSelected: {
        borderColor: (theme) => theme.palette.primary.main,
    },
    radioDot: {
        width: 10,
        height: 10,
        borderRadius: "50%",
        background: (theme) => theme.palette.primary.main,
    },
});
